/**
 * WallProduct — lean React route for the gallery-wall bundle page.
 *
 * Decoupled: renders from LOCAL state, uploads in PARALLEL to /api/wall/upload
 * (Cloudinary g_auto smart crop), and touches the app ONLY at checkout
 * (getImagesDB once -> real Checkout modal). Manual crop -> /api/wall/crop.
 *
 * Data model: `pool` holds every uploaded image; `slots` (length N) references
 * pool entries by localId. Extra photos beyond the N frames live in the pool
 * (shown in תמונות) and are dropped from the cart at checkout, so the order is
 * always EXACTLY N frames.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { enqueueResilientUpload, uploadBaseForAttempt } from "@helpers/uploadResilience";
import { useRecoilState, useSetRecoilState } from "recoil";
import { Helmet } from "react-helmet-async";
import { imageFrameState, imagesData, imagesBlobState, clientCroppedImagesState, validCoupon } from "@atoms";
import { imageCountState, netPriceState } from "@atoms/priceCalc";
import { removeImage } from "@api";
import { useDiscountConfig } from "@helpers/discounts";
import { useModal } from "@helpers/hooks/useModal";
import useDeliveryRange from "@helpers/hooks/useDeliveryRange";
import useWindowDimensions from "@helpers/hooks/windowDemensions";
import useWallSession from "./useWallSession";
import WallScene from "./WallScene";
import CropModal from "./CropModal";
import OnboardScreens from "../../Shared/Modals/OnboardScreens";
import MergeSnackbar from "../../Shared/components/MergeSnackbar";
import { FRAMES, DEFAULT_FRAME, priceBundle, money, frameByName, isLow, sceneById } from "./data";
import { PRODUCTS } from "../ProductsCatalog/products";
import { trackProductView, trackProductBuild } from "../../api/visitTracking";
import { centerSquare, faceSquare, objectPosition, preloadFaceModel } from "../../../utils/wallSmartcrop";
import useA11yWidgetAlign from "../../../utils/useA11yWidgetAlign";
import "./style.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const SITE = "https://blends.co.il";
const MAXPOOL = 50;
const LSset = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* ignore */ } };
const LSget = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };

// Resolve the discount tier's coupon+percent for a given frame count (mirrors the
// wall's resolveTierDiscount, but returns the coupon so we can apply it in the app's
// real checkout). `cfg` is the app discountConfig object ({ count: {discount,coupons,
// enableDefault,fallbackToLower} }). Returns null when no discount applies.
const resolveTierCoupon = (count, cfg) => {
  const keys = Object.keys(cfg || {}).map(Number).sort((a, b) => a - b);
  let maxTier = null;
  for (const k of keys) if (k <= count) maxTier = k;
  if (maxTier == null) return null;
  const t = cfg[maxTier];
  if (t.enableDefault) return { discount: t.discount, coupon: (t.coupons || [])[0] };
  if (t.fallbackToLower) {
    let fb = null;
    for (const k of keys) if (k < maxTier && cfg[k].enableDefault) fb = k;
    if (fb != null) return { discount: cfg[fb].discount, coupon: (cfg[fb].coupons || [])[0] };
  }
  return null;
};
let SEQ = 0;
const nextId = () => "l" + (++SEQ) + Math.random().toString(36).slice(2, 7);

const IconFrame = () => (<svg viewBox="0 0 24 24"><path fillRule="evenodd" clipRule="evenodd" d="M5 3.5h14a2 2 0 012 2v13a2 2 0 01-2 2H5a2 2 0 01-2-2v-13a2 2 0 012-2zm2 4a1 1 0 00-1 1v8a1 1 0 001 1h10a1 1 0 001-1v-8a1 1 0 00-1-1H7z" /></svg>);
const IconPhotos = () => (<svg viewBox="0 0 24 24"><path fillRule="evenodd" clipRule="evenodd" d="M5 4h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2zm3.4 3.2a1.6 1.6 0 100 3.2 1.6 1.6 0 000-3.2zM5.8 17.6h12.4l-3.9-5-2.8 3.3-1.8-2z" /></svg>);
const IconEffect = () => (<svg viewBox="0 0 24 24"><path d="M12 2.6l1.7 4.4 4.4 1.7-4.4 1.7L12 14.8l-1.7-4.4L5.9 8.7l4.4-1.7z" /><circle cx="18.5" cy="4.5" r="1.4" /><circle cx="5.5" cy="16" r="1.1" /></svg>);
const IconCheck = () => (<svg viewBox="0 0 24 24"><path fillRule="evenodd" clipRule="evenodd" d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.7 7.3a1 1 0 00-1.4-1.4l-4.8 4.8-2.1-2.1a1 1 0 00-1.4 1.4l2.8 2.8a1 1 0 001.4 0l5.5-5.5z" /></svg>);
// Low-res warning triangle (mirrors WallScene's LR_ICON) — flagged on gallery thumbs too.
const IconLowRes = (<svg viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9.5" x2="12" y2="14" /><line x1="12" y1="17.1" x2="12" y2="17.2" /></svg>);

// Batch-upload safety on iOS Safari: never hold many full-resolution decoded
// bitmaps or run many face detections at once — that OOM-kills the tab (white
// screen + auto-reload). We decode each pick ONCE to derive a small preview + a
// small detect canvas, release the full-res bitmap, and process uploads through a
// bounded concurrency pool. Before CDN upload we also cap the SHORT edge (see
// UPLOAD_MAX_SHORT_EDGE) so 5K phone originals don't stall mobile networks.
// Short edge — not long edge — so a full-height square crop of the result still
// carries ≥2000px per side and the 1600px Cloudinary print square never upsamples.
const PREVIEW_MAX = 1440; // grid/wall preview long-edge (px)
const DETECT_MAX = 1024;  // face-detection input long-edge (px)
const UPLOAD_MAX_SHORT_EDGE = 2000; // CDN upload short-edge cap (px); skip if already smaller
const UPLOAD_JPEG_Q = 0.88;
const UPLOAD_CONCURRENCY = 3; // face-detect + upload jobs allowed in flight at once

const loadImg = (url) =>
  new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = url;
  });

const canvasToUrl = (canvas) =>
  new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b ? URL.createObjectURL(b) : null), "image/jpeg", 0.85);
    } catch (e) {
      resolve(null);
    }
  });

const drawScaled = (src, maxEdge, edge = "long") => {
  const sw = src.naturalWidth || src.width;
  const sh = src.naturalHeight || src.height;
  const basis = Math.max(1, edge === "short" ? Math.min(sw, sh) : Math.max(sw, sh));
  const s = Math.min(1, maxEdge / basis);
  const cw = Math.max(1, Math.round(sw * s));
  const ch = Math.max(1, Math.round(sh * s));
  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext("2d");
  if (ctx) ctx.drawImage(src, 0, 0, cw, ch);
  return c;
};

// Decode a picked file ONCE -> natural dims + a downscaled preview blob URL + a
// small detect canvas; the full-res <img> then goes out of scope (GC'd) so we
// never retain N full-res bitmaps. Falls back to the original object URL if
// canvas/toBlob is unavailable.
async function readDims(file) {
  const objUrl = URL.createObjectURL(file);
  const im = await loadImg(objUrl);
  if (!im) return { objUrl, w: 0, h: 0, previewUrl: null, detCanvas: null };
  const w = im.naturalWidth;
  const h = im.naturalHeight;
  let previewUrl = null;
  let detCanvas = null;
  try {
    const previewCanvas = drawScaled(im, PREVIEW_MAX);
    detCanvas = Math.max(previewCanvas.width, previewCanvas.height) <= DETECT_MAX
      ? previewCanvas
      : drawScaled(previewCanvas, DETECT_MAX);
    previewUrl = await canvasToUrl(previewCanvas);
  } catch (e) {
    /* keep null -> preview falls back to the original object URL */
  }
  return { objUrl, w, h, previewUrl, detCanvas };
}

// Cap upload payload: phone cameras ship 4–6K originals, but print is ~1600px.
// Re-encode only when the SHORT edge is over UPLOAD_MAX_SHORT_EDGE (already-small
// files pass through; a crop square can never exceed the short edge, so capping it
// at 2000 keeps every possible crop at full print resolution).
// Crop region is scaled with the bitmap so Cloudinary c_crop still matches the
// wall preview. Crop-editor pixel space must match the uploaded file — callers
// should adopt returned w/h/region/objUrl when scaled=true.
async function prepareUploadFile(file, w, h, region) {
  const minEdge = Math.min(w || 0, h || 0);
  if (!file || !w || !h || minEdge <= UPLOAD_MAX_SHORT_EDGE) {
    return { file, w, h, region, scaled: false, objUrl: null };
  }
  const srcUrl = URL.createObjectURL(file);
  try {
    const im = await loadImg(srcUrl);
    if (!im || !(im.naturalWidth || im.width)) {
      return { file, w, h, region, scaled: false, objUrl: null };
    }
    const canvas = drawScaled(im, UPLOAD_MAX_SHORT_EDGE, "short");
    const nw = canvas.width;
    const nh = canvas.height;
    const scale = nw / w;
    const blob = await new Promise((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), "image/jpeg", UPLOAD_JPEG_Q);
      } catch (e) {
        resolve(null);
      }
    });
    if (!blob) return { file, w, h, region, scaled: false, objUrl: null };
    const base = (file.name || "photo.jpg").replace(/\.(heic|heif|png|webp|jpe?g)$/i, "");
    const outFile = new File([blob], `${base || "photo"}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
    let scaledRegion = region;
    if (region && region.size) {
      const size = Math.min(Math.round(region.size * scale), Math.min(nw, nh));
      scaledRegion = {
        x: Math.max(0, Math.min(nw - size, Math.round(region.x * scale))),
        y: Math.max(0, Math.min(nh - size, Math.round(region.y * scale))),
        size,
      };
    }
    return {
      file: outFile,
      w: nw,
      h: nh,
      region: scaledRegion,
      scaled: true,
      objUrl: URL.createObjectURL(blob),
    };
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
}

// Run async `worker` over `items` with at most `limit` in flight — bounds peak
// memory/GPU on iOS regardless of how many photos were picked.
function runWithLimit(items, limit, worker) {
  let idx = 0;
  const runner = async () => {
    while (idx < items.length) {
      const cur = idx++;
      // eslint-disable-next-line no-await-in-loop
      await worker(items[cur], cur);
    }
  };
  return Promise.all(Array.from({ length: Math.min(limit, items.length) }, runner));
}

const previewSrc = (e) => (e && ((e.record && e.record.crop_image) || e.previewUrl || e.objUrl)) || null;
const originalSrc = (e) => (e && (e.objUrl || (e.record && e.record.image))) || null;

const WallProduct = () => {
  const { bundle } = useParams();
  const [searchParams] = useSearchParams();
  const env = sceneById(searchParams.get("scene") || "living").file.replace(/\.webp$/, ""); // room
  const rowsParam = searchParams.get("rows"); // e.g. "1-2-3-4-3-2-1" diamond; null = rectangular
  const wallRows = useMemo(
    () => (rowsParam ? rowsParam.split("-").map((x) => parseInt(x, 10)).filter((x) => x > 0) : null),
    [rowsParam]
  );
  const brickParam = searchParams.get("brick"); // e.g. "8-7-8-7-6" staggered brick rows; null = not brick
  const wallBrick = useMemo(
    () => (brickParam ? brickParam.split("-").map((x) => parseInt(x, 10)).filter((x) => x > 0) : null),
    [brickParam]
  );
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const isMobile = width <= 760;
  const count = useMemo(() => parseInt(String(bundle || "").match(/^(\d+)/)?.[1] || "0", 10), [bundle]);

  // Prefer ?p= (catalog). If missing (ads / shared /products/N-frames links), resolve
  // from count + scene so CRO views still attribute to a catalog product.
  const productId = useMemo(() => {
    const fromUrl = searchParams.get("p");
    if (fromUrl) return fromUrl;
    const scene = searchParams.get("scene");
    const match = PRODUCTS.find((p) => {
      if (p.count !== count) return false;
      if (!scene) return true;
      if (p.scene === scene) return true;
      return (p.variations || []).some((v) => v.scene === scene);
    });
    return (match && match.id) || null;
  }, [searchParams, count]);
  const productName = (PRODUCTS.find((p) => p.id === productId) || {}).name || null;

  // Wall-product CRO: record a detail view once per product open (deduped
  // server-side per visitor/product/day). Silent — never blocks the page.
  useEffect(() => {
    if (!productId) return;
    trackProductView(productId, { scene: searchParams.get("scene") || "living" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const [frameChoose, setFrameChoose] = useRecoilState(imageFrameState);
  const setCoupon = useSetRecoilState(validCoupon);
  const { discountConfig: dcfg, defaultCoupon } = useDiscountConfig();
  const setImages = useSetRecoilState(imagesData);
  const setImageCount = useSetRecoilState(imageCountState);
  const setNetPrice = useSetRecoilState(netPriceState);
  const setImagesBlob = useSetRecoilState(imagesBlobState);
  const setClientCropped = useSetRecoilState(clientCroppedImagesState);
  const modal = useModal();
  const { ensureSession, uploadImage, updateCrop, hydrateForCheckout, restore } = useWallSession();
  useA11yWidgetAlign();

  const [pool, setPool] = useState([]); // all uploads: {localId,file,objUrl,record,w,h,low,pending,cropRegion}
  const [slots, setSlots] = useState(() => Array(count).fill(null)); // localId | null

  // Gallery-merge (Path B): after a merge, re-read the session's images and append
  // anything new to the pool. Slots/placement are never touched.
  const refreshPoolFromServer = useCallback(() => {
    restore().then((res) => {
      const imgs = res && res.data && res.data.data;
      if (!imgs || !imgs.length) return;
      setPool((prev) => {
        const known = new Set(prev.map((e) => e.record && e.record._id).filter(Boolean));
        const added = imgs
          .filter((im) => !known.has(im._id))
          .map((im) => ({
            localId: "s" + im._id, file: null, objUrl: null, record: im,
            w: +im.imagewidth, h: +im.imageheight, low: isLow(+im.imagewidth, +im.imageheight),
            pending: false, cropRegion: im.cropbox_data && im.cropbox_data.size ? im.cropbox_data : null,
            bw: !!im.bwFilter, faceReady: true,
          }));
        return added.length ? [...prev, ...added] : prev;
      });
    }).catch(() => {});
  }, [restore]);
  const [sheet, setSheet] = useState(null); // 'frame'|'effect'|'edit'|'photos'
  const [cartOpen, setCartOpen] = useState(false);
  const [cropFor, setCropFor] = useState(null); // slot index
  const [bw, setBw] = useState(false);
  const [effectFor, setEffectFor] = useState(null); // slot index -> per-image effect sheet; null = global bar
  const [replaceFor, setReplaceFor] = useState(null); // slot index -> gallery is open in "pick a replacement" mode
  const [zoomed, setZoomed] = useState(false); // scene zoomed in -> hide corner chrome
  const bwRef = useRef(false);
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true); // restoring the session -> spinner (no empty-state flash)
  const [showOnboard, setShowOnboard] = useState(false); // onboarding, shown once while the first batch uploads
  const editSlotRef = useRef(null);
  const swapFromRef = useRef(null);
  const swappingRef = useRef(false);
  const replacingRef = useRef(false);
  const targetSlotRef = useRef(null);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const sceneRef = useRef(null);
  const fcardsRef = useRef(null);
  const pendingUploads = useRef([]);
  // In-flight edit writes (per-image B&W, apply-to-all B&W). goCheckout awaits these
  // like uploads so the order never reads a stale crop/effect from a not-yet-committed
  // edit. Each entry: { p: Promise, ok: () => boolean-after-settle }.
  const pendingEdits = useRef([]);
  const hintTimer = useRef(null);

  // Keep the latest pool for unmount cleanup; revoke every object URL when the
  // wall page unmounts (SPA nav doesn't unload the document, so blob URLs leak).
  const poolRef = useRef([]);
  poolRef.current = pool;
  useEffect(() => () => {
    poolRef.current.forEach((e) => {
      if (e && e.objUrl) URL.revokeObjectURL(e.objUrl);
      if (e && e.previewUrl) URL.revokeObjectURL(e.previewUrl);
    });
  }, []);

  const frameName = frameChoose || DEFAULT_FRAME;
  const frameMeta = frameByName(frameName);
  const price = useMemo(() => priceBundle(count), [count]);
  // Delivery estimate from the app's single source of truth (server config: holidays,
  // admin overrides, per-frame lead times) so the wall matches checkout/review exactly.
  const deliv = useDeliveryRange(frameName);

  // Topbar discount reveal — FIRST VISIT ONLY. On the first wall page a visitor sees
  // (with a real bundle discount), the full price lingers ~1s, then the discounted
  // price slides in from the side, takes its place, and the original gets struck
  // through. Consumes a one-time localStorage flag so returning visitors go straight
  // to the end state. (To re-test locally, clear "blends:wallDiscountRevealed".)
  const [revealDiscount] = useState(() => {
    if (typeof window === "undefined" || !(price.savings > 0)) return false;
    try {
      const KEY = "blends:wallDiscountRevealed";
      if (localStorage.getItem(KEY)) return false;
      localStorage.setItem(KEY, "1");
      return true;
    } catch { return false; }
  });

  const showHint = useCallback((m, persist) => {
    setHint(m);
    clearTimeout(hintTimer.current);
    if (!persist) hintTimer.current = setTimeout(() => setHint(null), 3200);
  }, []);
  const hideHint = useCallback(() => { clearTimeout(hintTimer.current); setHint(null); }, []);

  const unfocus = useCallback(() => { if (sceneRef.current) sceneRef.current.unfocus(); }, []);
  // Clearing replaceFor here is the safety net: closing the gallery any way (Done,
  // tapping the backdrop, switching sheets) exits replace mode, so a stray target can
  // never linger and hijack a later normal gallery tap.
  const closeSheets = useCallback(() => { setSheet(null); setEffectFor(null); setReplaceFor(null); unfocus(); }, [unfocus]);
  // Switch sheets from the toolbar — always clear any focused-tile dark overlay + replace mode.
  const openSheet = useCallback((name) => { unfocus(); setReplaceFor(null); setSheet(name); }, [unfocus]);
  const openEdit = useCallback((slot) => {
    editSlotRef.current = slot;
    if (sceneRef.current) sceneRef.current.focusTile(slot);
    setSheet("edit");
  }, []);

  useEffect(() => { if (!count || count < 1 || count > MAXPOOL) navigate("/", { replace: true }); }, [count, navigate]);

  // Measure the real fixed header so topbar + mobile scene height line up.
  useEffect(() => {
    const measure = () => {
      const h = document.querySelector(".site-header")?.offsetHeight;
      if (h && rootRef.current) rootRef.current.style.setProperty("--wallHeaderH", h + "px");
    };
    measure();
    const timers = [300, 1200, 2500].map((ms) => setTimeout(measure, ms));
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("resize", measure); timers.forEach(clearTimeout); };
  }, []);

  // Mount: isolate atoms, seed frame, load the user's ONE gallery (the app session's
  // cart) — show ALL its images in the pool, auto-place the first N onto the frames.
  useEffect(() => {
    preloadFaceModel(); // warm the face model so the first crop refine is instant
    // #4: a shared/returned wall URL carries ?s=<sessionId> (the wall's id) — seed it so
    // restore() reloads that wall's images on this device.
    const urlSid = searchParams.get("s");
    if (urlSid && !LSget("sessionId")) LSset("sessionId", urlSid);
    setImages([]); setImageCount(0); setImagesBlob([]); setClientCropped({});
    // Frame on open: the user's REMEMBERED choice for THIS product wins (wallFrame:<id>);
    // else the product's default (the catalog wrote it to frameSelected before navigating);
    // else the app default. So it matches the products page unless the user already picked
    // a different frame for this product — and that per-product choice sticks across visits.
    setFrameChoose((productId && LSget("wallFrame:" + productId)) || LSget("frameSelected") || DEFAULT_FRAME);
    // Effect on open: same rule as the frame — the user's REMEMBERED choice for THIS product
    // wins (wallBw:<id>), else the catalog variation's default (?bw=1 → e.g. calm opens B&W),
    // else off. A restored session that already has the B&W flag still forces it on below.
    const bwRemembered = productId ? LSget("wallBw:" + productId) : null;
    const initBw = bwRemembered != null ? bwRemembered === "1" : searchParams.get("bw") === "1";
    setBw(initBw); bwRef.current = initBw;
    restore().then((res) => {
      const imgs = res && res.data && res.data.data;
      if (!imgs || !imgs.length) return;
      if (imgs.some((im) => im.bwFilter)) { setBw(true); bwRef.current = true; }
      const entries = imgs.map((im) => ({
        localId: "s" + im._id, file: null, objUrl: null, record: im,
        w: +im.imagewidth, h: +im.imageheight, low: isLow(+im.imagewidth, +im.imageheight),
        pending: false, cropRegion: im.cropbox_data && im.cropbox_data.size ? im.cropbox_data : null,
        bw: !!im.bwFilter, faceReady: true,
      }));
      setPool(entries);
      setSlots(() => { const n = Array(count).fill(null); entries.slice(0, count).forEach((e, i) => { n[i] = e.localId; }); return n; });
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entryById = useMemo(() => { const m = {}; pool.forEach((e) => { m[e.localId] = e; }); return m; }, [pool]);
  const slotsView = useMemo(() => {
    // Instant local preview, smart-cropped via object-position (what the user
    // sees the moment they pick). Reload / manual crop uses the server c_crop
    // square (already pre-cropped, so centered). Same region either way -> no jump.
    const arr = slots.map((id) => {
      const e = id ? entryById[id] : null;
      if (!e) return null;
      // Only use the LOCAL preview if it's actually decodable: a downscaled previewUrl,
      // or an objUrl whose dims we read (w>0). A HEIC on desktop yields previewUrl=null
      // and w=0 -> fall through to the server crop_image (JPEG) instead of a blank frame.
      // Option A (no jump): hold the shimmer until face detection resolves the crop
      // (faceReady), so the first paint is already the final object-position.
      const hasLocalPreview = (e.previewUrl || (e.objUrl && e.w > 0)) && e.faceReady;
      // Honest sync states: a slot NEVER shows the photo before the server confirmed
      // it has it — plain white + gray spinner until then. The photo pops in the
      // moment `record` lands; a job that can't complete is dropped from the wall.
      if (!e.record) return { uploading: true, low: e.low, bw: !!e.bw };
      if (hasLocalPreview && !e.manualCropped) return { src: e.previewUrl || e.objUrl, objPos: e.objPos || "50% 50%", low: e.low, bw: !!e.bw };
      return { src: (e.record && e.record.crop_image) || null, objPos: "50% 50%", low: e.low, bw: !!e.bw };
    });
    arr.__sw = frameMeta.sw;
    return arr;
  }, [slots, entryById, frameMeta.sw]);
  const placedIds = useMemo(() => new Set(slots.filter(Boolean)), [slots]);
  const placedCount = placedIds.size;
  const remaining = count - placedCount;

  // Wall-product CRO: fire a one-time "build started" event the moment the first
  // image lands on the wall — the View → Build → Cart → Purchase mid-funnel step.
  const buildTrackedRef = useRef(false);
  useEffect(() => {
    if (!productId || buildTrackedRef.current || placedCount < 1) return;
    buildTrackedRef.current = true;
    trackProductBuild(productId, { scene: searchParams.get("scene") || "living" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placedCount, productId]);
  const full = remaining <= 0;

  // Batch upload progress -> spinner while any pick is still being processed/uploaded.
  const preparingCount = useMemo(() => pool.filter((e) => e.pending).length, [pool]);
  const preparing = preparingCount > 0;
  // B — faster reveal: the big cover waits only for CROPS to resolve (faceReady, ~1-2s)
  // then reveals the wall; the slower uploads finish afterwards under a small N/N bar.
  const batchNew = useMemo(() => pool.filter((e) => e.file), [pool]);           // this session's uploads
  const cropsReady = useMemo(() => batchNew.filter((e) => e.faceReady).length, [batchNew]);
  const uploadsDone = useMemo(() => batchNew.filter((e) => !e.pending && e.record).length, [batchNew]);
  const batchProcessing = batchNew.length > 0 && cropsReady < batchNew.length; // some crop still resolving

  // #4: once the wall has images, make its URL unique by embedding the session id (the
  // wall's id) so it's identifiable / returnable. Runs again as uploads settle (by which
  // point ensureSession has written the sessionId). replaceState = no history spam.
  useEffect(() => {
    if (!pool.length || typeof window === "undefined") return;
    const sid = LSget("sessionId");
    if (!sid) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("s") === sid) return;
      url.searchParams.set("s", sid);
      window.history.replaceState(null, "", url.toString());
    } catch (e) { /* ignore */ }
  }, [pool.length, preparing]);

  // --- upload (parallel, background); extras land in the pool (unplaced) ---
  const makeEntries = useCallback(async (files) => {
    const out = [];
    for (let i = 0; i < files.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      const { objUrl, previewUrl, detCanvas, w, h } = await readDims(files[i]);
      const region = centerSquare(w, h); // instant fallback, refined by faceSquare
      out.push({ localId: nextId(), file: files[i], objUrl, previewUrl, detCanvas, record: null, w, h, low: isLow(w, h), pending: true, region, objPos: objectPosition(region, w, h), cropRegion: null, bw: bwRef.current, faceReady: false });
    }
    return out;
  }, []);

  const startUploads = useCallback((entries) => {
    // Bounded concurrency: at most UPLOAD_CONCURRENCY face-detect+upload jobs run
    // at once, so peak memory/GPU stays flat no matter how many photos were picked
    // (12+ full-res in parallel OOM-kills iOS Safari). Detection runs on the small
    // detCanvas, not the full-res image; the face box is mapped back to original px.
    // Then we may downscale the file (UPLOAD_MAX_SHORT_EDGE) before CDN upload.
    const batch = runWithLimit(entries, UPLOAD_CONCURRENCY, async (entry) => {
      // refine the crop with face detection (fast once the model is preloaded),
      // then upload (possibly downscaled) + that region so the print matches the preview.
      let region = entry.region;
      let uploadW = entry.w;
      let uploadH = entry.h;
      let uploadFile = entry.file;
      try {
        if (entry.detCanvas) region = await faceSquare(entry.detCanvas, entry.w, entry.h);
      } catch (err) { /* keep center crop */ }
      entry.detCanvas = null; // detection done -> release the canvas
      // faceReady flips the frame from shimmer to the image at its FINAL crop — so the
      // first paint is already face-centred (no center->face jump). See slotsView.
      setPool((prev) => prev.map((e) => (e.localId === entry.localId ? { ...e, region, objPos: objectPosition(region, e.w, e.h), detCanvas: null, faceReady: true } : e)));
      try {
        const prepared = await prepareUploadFile(uploadFile, uploadW, uploadH, region);
        uploadFile = prepared.file;
        uploadW = prepared.w;
        uploadH = prepared.h;
        region = prepared.region;
        if (prepared.scaled) {
          // Align crop-editor pixel space with what we shipped to CDN; keep previewUrl.
          setPool((prev) => prev.map((e) => {
            if (e.localId !== entry.localId) return e;
            if (e.objUrl) URL.revokeObjectURL(e.objUrl);
            return {
              ...e,
              file: uploadFile,
              w: uploadW,
              h: uploadH,
              region,
              objPos: objectPosition(region, uploadW, uploadH),
              objUrl: prepared.objUrl || e.objUrl,
              low: isLow(uploadW, uploadH),
            };
          }));
        }
        // Resilient transport: the slot shows a white frame + spinner until `record`
        // lands. EVERY failure (connection or server) auto-retries with backoff and
        // wakes instantly on connectivity signals — zero taps. If the job can't
        // complete within its window it's dropped: the frame is removed from the
        // wall (same as a refresh would). NOTE: before this, a failed upload set
        // pending:false with no record and silently looked "done".
        enqueueResilientUpload({
          id: "wall-" + entry.localId,
          meta: {
            page: "wall",
            filename: (entry.file && entry.file.name) || "",
            sizeKB: Math.round(((uploadFile && uploadFile.size) || 0) / 1024),
            sessionId: LSget("sessionId") || "",
            uid: LSget("uniqueUserId") || "",
          },
          attempt: (net) => uploadImage(uploadFile, uploadW, uploadH, bwRef.current, region, { ...net, baseUrl: uploadBaseForAttempt(net.attemptNo) }),
          onDone: (rec) => {
            setPool((prev) => prev.map((e) => (e.localId === entry.localId ? { ...e, record: rec, pending: false } : e)));
          },
          onDrop: () => {
            setPool((prev) => prev.filter((x) => x.localId !== entry.localId));
            setSlots((prev) => prev.map((sid) => (sid === entry.localId ? null : sid)));
            showHint("תמונה לא הצליחה לעלות — נסו להעלות אותה שוב");
          },
        });
      } catch (err) {
        // Local preparation failed (decode/canvas) — drop the frame, same contract.
        setPool((prev) => prev.filter((x) => x.localId !== entry.localId));
        setSlots((prev) => prev.map((sid) => (sid === entry.localId ? null : sid)));
        showHint("תמונה לא הצליחה לעלות — נסו להעלות אותה שוב");
      }
    });
    pendingUploads.current.push(batch);
  }, [uploadImage, showHint]);

  const addFiles = useCallback(async (files, target) => {
    const list = [...files];
    if (!list.length) return;
    const room = MAXPOOL - pool.length;
    if (room <= 0) { showHint(`אפשר עד ${MAXPOOL} תמונות`); return; }
    const take = list.slice(0, room);
    if (list.length > take.length) showHint(`אפשר עד ${MAXPOOL} תמונות`);
    // Create the session + cart up-front (before the heavy decode/upload) so any
    // upload that completes is persisted even if the tab is reloaded mid-batch.
    // Then stamp the product being designed on the cart (wallProductId) so the
    // server can price the cart at product.count frames for CRM prospect capture
    // even while photos are still uploading.
    ensureSession()
      .then((s) => {
        if (s && s.sid && productId) {
          return axios.post(`${BASE_URL}/addtocart`, {
            uid: s.uid,
            sessionId: s.sid,
            wallProductId: productId,
          });
        }
      })
      .catch(() => {});
    // Uploading a batch is a natural moment to onboard + collect the user's info (name/
    // gift/date) while photos upload. Show it whenever the user hasn't given their info
    // yet — rely on the existing localStorage flag (loginCompleted), not a first-upload
    // guard. Any complete/skip sets loginCompleted, so it won't re-prompt afterwards.
    if (localStorage.getItem("loginCompleted") !== "1") setShowOnboard(true);
    const entries = await makeEntries(take);
    setPool((prev) => [...prev, ...entries]);
    setSlots((prev) => {
      const n = prev.slice();
      let ei = 0;
      if (target != null && !n[target] && ei < entries.length) n[target] = entries[ei++].localId;
      for (let i = 0; i < n.length && ei < entries.length; i++) if (!n[i]) n[i] = entries[ei++].localId;
      return n; // leftover entries stay unplaced (pool -> תמונות)
    });
    startUploads(entries);
  }, [pool.length, makeEntries, startUploads, showHint, ensureSession, productId]);

  // Replace slot's image; first picked replaces it (old deleted), extras -> pool.
  const replaceAt = useCallback(async (slot, files) => {
    const list = [...files];
    if (!list.length) return;
    const oldId = slots[slot];
    const oldEntry = oldId ? entryById[oldId] : null;
    if (oldEntry) {
      if (oldEntry.objUrl) URL.revokeObjectURL(oldEntry.objUrl);
      if (oldEntry.previewUrl) URL.revokeObjectURL(oldEntry.previewUrl);
    }
    if (oldEntry && oldEntry.record && oldEntry.record._id) removeImage(oldEntry.record._id).catch(() => {});
    const room = MAXPOOL - (pool.length - (oldEntry ? 1 : 0));
    const entries = await makeEntries(list.slice(0, Math.max(1, room)));
    if (!entries.length) return;
    setPool((prev) => [...prev.filter((e) => e.localId !== oldId), ...entries]);
    setSlots((prev) => {
      const n = prev.slice();
      n[slot] = entries[0].localId;
      let ei = 1;
      for (let i = 0; i < n.length && ei < entries.length; i++) if (!n[i] && i !== slot) n[i] = entries[ei++].localId;
      return n;
    });
    startUploads(entries);
  }, [slots, entryById, pool.length, makeEntries, startUploads]);

  const onFileChange = useCallback((e) => {
    const files = e.target.files;
    if (replacingRef.current) {
      replacingRef.current = false;
      const slot = editSlotRef.current;
      if (slot != null && files.length) replaceAt(slot, files);
    } else {
      addFiles(files, targetSlotRef.current);
    }
    targetSlotRef.current = null;
    e.target.value = "";
  }, [addFiles, replaceAt]);

  const openPicker = useCallback((slot) => {
    // A plain add is never a replace — clear any stale replace flag left by a
    // cancelled upload-replace, so this pick can't be mis-handled as a replacement.
    replacingRef.current = false;
    targetSlotRef.current = slot != null ? slot : null;
    if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); }
  }, []);

  // Delete an image entirely (from wall + pool + server).
  const deleteEntry = useCallback((localId) => {
    const e = entryById[localId];
    if (e) {
      if (e.objUrl) URL.revokeObjectURL(e.objUrl);
      if (e.previewUrl) URL.revokeObjectURL(e.previewUrl);
    }
    setSlots((prev) => prev.map((id) => (id === localId ? null : id)));
    setPool((prev) => prev.filter((x) => x.localId !== localId));
    if (e && e.record && e.record._id) removeImage(e.record._id).catch(() => {});
  }, [entryById]);

  // Toggle placement from the תמונות tray (place / remove-from-wall; keeps in pool).
  const togglePlace = useCallback((localId) => {
    if (placedIds.has(localId)) {
      setSlots((prev) => prev.map((id) => (id === localId ? null : id)));
    } else {
      if (full) { showHint("הקיר מלא · הסירו תמונה קודם"); return; }
      setSlots((prev) => { const n = prev.slice(); const empty = n.findIndex((x) => !x); if (empty >= 0) n[empty] = localId; return n; });
    }
  }, [placedIds, full, showHint]);

  // --- slot click ---
  const onSlotClick = useCallback((i) => {
    if (swappingRef.current) {
      swappingRef.current = false;
      const from = swapFromRef.current;
      if (from != null && from !== i && slots[i]) {
        setSlots((prev) => { const n = prev.slice(); const t = n[from]; n[from] = n[i]; n[i] = t; return n; });
        showHint("הוחלף");
      } else hideHint();
      return;
    }
    if (slots[i]) openEdit(i); else openPicker(i);
  }, [slots, openEdit, openPicker, showHint, hideHint]);

  // --- frame selection (server sync) ---
  const selectFrame = useCallback(async (name) => {
    setFrameChoose(name);
    LSset("frameSelected", name);
    if (productId) LSset("wallFrame:" + productId, name); // remember the user's choice PER product
    const uid = LSget("uniqueUserId");
    const sid = LSget("sessionId");
    if (!uid) return;
    try {
      await axios.post(`${BASE_URL}/frameselected`, { userid: uid, type: name });
      if (sid) await axios.put(`${BASE_URL}/addtocart/session/${sid}`, { frameType: name });
    } catch (e) { /* best-effort */ }
  }, [setFrameChoose, productId]);

  // Record an in-flight edit write so goCheckout can await it (and know if it failed)
  // before building the order — otherwise a just-tapped B&W toggle can lose the race.
  const trackEdit = useCallback((promise) => {
    const rec = { ok: true };
    rec.p = Promise.resolve(promise).catch(() => { rec.ok = false; });
    pendingEdits.current.push(rec);
    return rec.p;
  }, []);

  // --- effect (global B&W): instant CSS grayscale preview + ONE lightweight call to
  //     persist the bwFilter FLAG across the gallery. No per-image Cloudinary transform
  //     (the print service applies the flag at print time). ---
  const setEffect = useCallback((on) => {
    setBw(on);
    bwRef.current = on;
    setPool((prev) => prev.map((e) => ({ ...e, bw: on }))); // per-image state mirrors the "apply to all" choice
    if (productId) LSset("wallBw:" + productId, on ? "1" : "0"); // remember the choice PER product
    const uid = LSget("uniqueUserId");
    const sid = LSget("sessionId");
    if (uid || sid) trackEdit(axios.post(`${BASE_URL}/wall/bw`, { uid, sessionId: sid, bw: on }));
  }, [productId, trackEdit]);

  // Per-image effect: flip ONE image's B&W. Real — the print service reads bwFilter;
  // /wall/crop preserves the stored crop and just updates the flag (null region).
  const setImageEffect = useCallback((slot, on) => {
    const localId = slot != null ? slots[slot] : null;
    const e = localId ? entryById[localId] : null;
    if (!e) return;
    setPool((prev) => prev.map((x) => (x.localId === localId ? { ...x, bw: on } : x)));
    if (e.record && e.record.publicId) trackEdit(updateCrop(e.record.publicId, null, on));
  }, [slots, entryById, updateCrop, trackEdit]);

  // --- edit-sheet actions ---
  const editCrop = useCallback(() => {
    const slot = editSlotRef.current;
    const e = slot != null ? entryById[slots[slot]] : null;
    if (!e || !originalSrc(e) || e.pending) { showHint("התמונה עדיין נטענת"); return; }
    setSheet(null);
    setCropFor(slot);
  }, [slots, entryById, showHint]);

  const editSwap = useCallback(() => {
    closeSheets(); // unfocus this tile; swap mode has its own hint
    swappingRef.current = true;
    swapFromRef.current = editSlotRef.current;
    showHint("בחרו מסגרת להחלפה", true); // persist until a frame is chosen
  }, [closeSheets, showHint]);

  const editReplace = useCallback(() => {
    const slot = editSlotRef.current;
    // If the gallery holds spare (unplaced) photos, open it so the user can swap one
    // into this frame; otherwise fall straight through to the file picker (upload-
    // replace). editSlotRef stays the target for the upload path.
    const hasExcess = slot != null && pool.some((e) => !placedIds.has(e.localId));
    if (hasExcess) {
      setReplaceFor(slot);
      unfocus();
      setSheet("photos");
      showHint("בחרו תמונה חדשה למסגרת");
      return;
    }
    // close the sheet + clear focus up-front so the dark overlay can't stick
    // even if the file picker is cancelled.
    replacingRef.current = true;
    setSheet(null);
    unfocus();
    if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); }
  }, [pool, placedIds, unfocus, showHint]);

  // Replace-from-gallery: swap the picked SPARE photo into the target frame; the
  // frame's old photo drops back to the pool (unplaced), so the wall stays full and
  // nothing is deleted. Placed photos aren't candidates (they already occupy a frame).
  const pickForReplace = useCallback((localId) => {
    const slot = replaceFor;
    if (slot == null) return;
    if (placedIds.has(localId)) { showHint("התמונה כבר על הקיר"); return; }
    setSlots((prev) => { const n = prev.slice(); n[slot] = localId; return n; });
    closeSheets(); // exits replace mode (clears replaceFor)
  }, [replaceFor, placedIds, showHint, closeSheets]);

  // In replace mode the gallery's "+" uploads a NEW photo straight into the target
  // frame (upload-replace) instead of just adding it to the pool — then exits replace
  // mode so it can't linger.
  const replaceViaUpload = useCallback(() => {
    const slot = replaceFor;
    if (slot == null) { openPicker(null); return; }
    editSlotRef.current = slot;
    replacingRef.current = true;
    setReplaceFor(null);
    setSheet(null);
    unfocus();
    if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); }
  }, [replaceFor, openPicker, unfocus]);

  const editRemove = useCallback(() => {
    const slot = editSlotRef.current;
    closeSheets();
    if (slot != null && slots[slot]) deleteEntry(slots[slot]);
  }, [closeSheets, slots, deleteEntry]);

  const editEffect = useCallback(() => {
    const slot = editSlotRef.current;
    const e = slot != null ? entryById[slots[slot]] : null;
    if (!e || e.pending) { showHint("התמונה עדיין נטענת"); return; }
    setEffectFor(slot); // scope the effect sheet to this image (tile stays focused)
    setSheet("effect");
  }, [slots, entryById, showHint]);

  const onCropSave = useCallback(async (region) => {
    const slot = cropFor;
    const localId = slot != null ? slots[slot] : null;
    const e = localId ? entryById[localId] : null;
    if (!e) { setCropFor(null); unfocus(); return; }
    if (e.record && e.record.publicId) {
      try {
        // Preserve THIS image's B&W, not the global "apply to all" flag — otherwise
        // saving a crop/zoom on an image the user made B&W (per-image effect) resets
        // it back to colour (updateCrop rewrites bwFilter on every crop save).
        const res = await updateCrop(e.record.publicId, region, !!e.bw);
        // manual crop can zoom, which object-position can't express -> show the
        // server c_crop square for this slot instead of the local original.
        setPool((prev) => prev.map((x) => (x.localId === localId
          ? { ...x, cropRegion: region, region, manualCropped: true, record: { ...x.record, crop_image: res.cropImageUrl, view_image: res.viewImageUrl } } : x)));
      } catch (err) { showHint("שגיאה בשמירת החיתוך"); }
    } else {
      setPool((prev) => prev.map((x) => (x.localId === localId ? { ...x, cropRegion: region } : x)));
    }
    setCropFor(null);
    unfocus();
  }, [cropFor, slots, entryById, updateCrop, showHint, unfocus]);

  // --- checkout: server validates the placed SUBSET against the product requirement,
  //     then we charge for exactly N (the gallery can hold more; it isn't ordered) ---
  const goCheckout = useCallback(async () => {
    if (placedCount < count) { showHint(`נדרשות ${count} תמונות`); return; }
    setBusy(true);
    try {
      await Promise.allSettled(pendingUploads.current);
      pendingUploads.current = [];
      // Wait for in-flight edit writes (B&W toggles) so the order reads the COMMITTED
      // effect, not a stale one — the same guarantee uploads already have.
      const edits = pendingEdits.current;
      pendingEdits.current = [];
      await Promise.allSettled(edits.map((r) => r.p));

      // Every PLACED frame must be server-confirmed — a spinner/↻ frame can't be
      // paid for. (pendingUploads settle at enqueue time now; pool records are the
      // source of truth for what the server actually has.)
      const placedAll = slots.map((id) => (id ? entryById[id] : null)).filter(Boolean);
      const unsyncedPlaced = placedAll.filter((e) => !e.record);
      if (unsyncedPlaced.length) {
        showHint("התמונות עדיין נשמרות");
        return;
      }

      // The N placed images (publicId === server imageId), in slot order.
      const placedEntries = slots
        .map((id) => (id ? entryById[id] : null))
        .filter((e) => e && e.record);
      const placedRecords = placedEntries.map((e) => e.record);
      const placedImageIds = placedRecords.map((r) => r.publicId).filter(Boolean);
      if (placedImageIds.length !== count) { showHint(`נדרשות ${count} תמונות`); return; }

      // If any effect write failed, don't ship a stale effect: reconcile each placed
      // image's B&W from the wall (what the user actually sees) using its CURRENT state,
      // and block checkout if that still can't persist.
      if (edits.some((r) => !r.ok)) {
        try {
          await Promise.all(placedEntries.map((e) => updateCrop(e.record.publicId, null, !!e.bw)));
        } catch (_) {
          pendingEdits.current = edits.filter((r) => !r.ok); // keep the failures for a retry
          showHint("שגיאה בשמירת עריכה, נסו שוב");
          return;
        }
      }

      // Validate + flag the subset server-side (authoritative product requirement).
      const sid = LSget("sessionId");
      const prep = await axios
        .post(`${BASE_URL}/wall/prepare-order`, { sessionId: sid, productId, count, frame: frameName, placedImageIds })
        .then((r) => r.data)
        .catch((e) => (e.response && e.response.data) || { success: false });
      if (!prep || !prep.success) {
        setCoupon(null);
        if (prep && prep.error === "count_mismatch") showHint(`נדרשות ${prep.required || count} תמונות`);
        else showHint("שגיאה בהכנת ההזמנה — רעננו ונסו שוב");
        return;
      }

      // Base atoms (frame prices) from the session, then OVERRIDE the cart to the placed
      // subset so the real checkout charges for exactly N — not the whole gallery. The
      // server likewise orders only this subset (selectOrderImages / includedInOrder).
      await hydrateForCheckout();
      setImages(placedRecords);
      setImageCount(count);
      setNetPrice(price.net); // charge base = N frames (matches the wall's displayed price)

      // Tier coupon for the validated N so the charge reflects the bundle discount.
      const tc = resolveTierCoupon(count, dcfg);
      if (tc && tc.coupon && tc.discount > 0) {
        // Show the discount-config promo name (e.g. "מבצעי הקיץ") as the single source of
        // truth, not the raw internal tier code (e.g. GIFT20). Discount amount is unchanged.
        setCoupon({ coupon: defaultCoupon || tc.coupon, discount: tc.discount, discountPrice: price.net * (tc.discount / 100), source: "static" });
      } else {
        setCoupon(null);
      }
      setFrameChoose(frameName);
      LSset("frameSelected", frameName);
      setCartOpen(false);
      modal("open", isMobile ? "mobileCheckout" : "checkout");
    } finally { setBusy(false); }
  }, [placedCount, count, slots, entryById, showHint, hydrateForCheckout, updateCrop, setImages, setImageCount, setNetPrice, productId, frameName, setFrameChoose, modal, isMobile, dcfg, setCoupon, price]);

  const onCta = useCallback(() => { if (full) setCartOpen(true); else openPicker(null); }, [full, openPicker]);
  // Frame-selector carousel: arrow scroll + bring the SELECTED frame into view on open.
  const scrollFcards = useCallback((delta) => {
    const c = fcardsRef.current;
    if (c) c.scrollBy({ left: -delta * (c.clientWidth * 0.7), behavior: "smooth" });
  }, []);
  useEffect(() => {
    if (sheet !== "frame") return undefined;
    const t = setTimeout(() => {
      const c = fcardsRef.current;
      const sel = c && c.querySelector(".fcard.sel");
      if (!c || !sel) return;
      // Horizontal-only scroll of the carousel — never scrollIntoView, which can
      // scroll the whole page vertically and shove the layout.
      const cr = c.getBoundingClientRect();
      const sr = sel.getBoundingClientRect();
      c.scrollLeft += (sr.left + sr.width / 2) - (cr.left + cr.width / 2);
    }, 40);
    return () => clearTimeout(t);
  }, [sheet]);

  const ctaLabel = full ? `המשך לתשלום · ₪${money(price.final)}` : `העלו ${remaining} תמונות`;
  const features = [
    price.freeShip ? "משלוח חינם עד הבית" : "משלוח ₪35",
    "הדבקה קלה לקיר – בלי קידוח",
    "איכות הדפסה פרימיום",
    `${count} מסגרות · 20×20 ס"מ`,
  ];

  const FrameRow = ({ cls }) => FRAMES.map((f) => (
    <div key={f.name} className={`${cls}${f.name === frameName ? " sel" : ""}`} onClick={() => selectFrame(f.name)}>
      {f.badge ? <span className={cls === "fsel-item" ? `badge ${f.badge.cls}` : `fbadge ${f.badge.cls}`}>{f.badge.txt}</span> : null}
      <img src={`/assets/file/images/${f.img}`} alt={f.text} loading="lazy" />
      <p>{f.text}</p>
    </div>
  ));

  const cropEntry = cropFor != null ? entryById[slots[cropFor]] : null;

  // Per-image effect sheet: when effectFor is a slot, the chooser reflects + edits THAT
  // image's live B&W; otherwise it's the global "apply to all" bar.
  const effectSingle = effectFor != null;
  const effectEntry = effectSingle ? entryById[slots[effectFor]] : null;
  const effectCurBw = effectSingle ? !!(effectEntry && effectEntry.bw) : bw;
  const effectPrev = (effectSingle && effectEntry && previewSrc(effectEntry)) || "/assets/file/images/couple.png";
  const applyEffect = effectSingle ? (v) => setImageEffect(effectFor, v) : (v) => setEffect(v);

  return (
    <div className="wallp" ref={rootRef}>
      <Helmet>
        <title>{`Blends – סט ${count} מסגרות תמונה נדבקות לקיר | 20×20 ס"מ`}</title>
        <link rel="canonical" href={`${SITE}/products/${count}-frames`} />
      </Helmet>

      <div className="topbar">
        <div className={`tb-price${revealDiscount ? " reveal" : ""}`} onClick={goCheckout}>
          {price.savings > 0 ? (
            <>
              {/* hero slot: the full price (tb-orig) sits under the discounted price
                  (tb-now); on reveal the discounted price swaps into its place */}
              <span className="tb-hero">
                <span className="tb-orig" aria-hidden="true">₪{money(price.regular)}</span>
                <span className="tb-now">₪{money(price.final)}</span>
              </span>
              <span className="tb-was">₪{money(price.regular)}<span className="tb-strike" aria-hidden="true" /></span>
            </>
          ) : (
            <span className="tb-now">₪{money(price.final)}</span>
          )}
        </div>
        <div className="tb-deliv">צפי משלוח <b>{isMobile ? deliv.mobileRange : deliv.desktopRange}</b></div>
      </div>

      <div className="card">
        <div className="info">
          <h1>סט {count} מסגרות תמונה נדבקות לקיר</h1>
          <p>מעלים את התמונות שלכם – אנחנו מדפיסים ושולחים {count} מסגרות מוכנות להדבקה. 20×20 ס"מ.</p>
          <ul className="feat">
            {features.map((f) => (
              <li key={f}>
                <img className="feat-tick" src="/assets/images/checkout_check.svg" alt="" aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="media">
          <WallScene ref={sceneRef} count={count} frameName={frameName} env={env} slots={slotsView} rows={wallRows} brick={wallBrick} onSlotClick={onSlotClick} onZoom={setZoomed} />

          {/* back to catalog + product name — hidden while zoomed in so they don't sit over the enlarged photo */}
          <button type="button" className={`wallp-back${zoomed ? " zhide" : ""}`} aria-label="חזרה לקטלוג" onClick={() => navigate("/products")}>
            <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          <div className={`wallp-pname${zoomed ? " zhide" : ""}`}>{productName || `${count} מסגרות · ${frameMeta.text}`}</div>

          {/* Phase 2 — uploads finishing in the background after the wall revealed (crops
              done). Compact N/N tucked under the product name; non-intrusive (UX research). */}
          {!batchProcessing && preparing && !showOnboard ? (
            <div className={`wallp-uploading${zoomed ? " zhide" : ""}`} role="status" aria-live="polite">
              <span className="wallp-uploading-spin" />
              <span>{uploadsDone}/{batchNew.length}</span>
            </div>
          ) : null}

          {/* restoring the session -> spinner over the scene, so the empty-state prompt
              never flashes before the images arrive */}
          {loading ? <div className="wallp-loading"><span className="wallp-loading-spin" /></div> : null}

          {/* Immersive controls live INSIDE the scene so menus/CTA/sheets emerge from
              and sit at the scene's bottom (desktop: absolute in the stage, clipped by
              its overflow; mobile: fixed to the viewport — unchanged). */}
          <button type="button" className={`wallp-startbtn${placedCount === 0 && !loading ? " show" : ""}`} onClick={() => openPicker(null)}>העלו {count} תמונות</button>

          {/* upload progress — sits where the empty-state upload button is (hidden while
              onboarding covers the screen; the frosted top bar takes over then) */}
          {batchProcessing && !showOnboard ? (
            // Big frosted-blur cover for the fast CENTERING phase (crops) only, so the wall
            // reveals quickly; the slower uploads then finish under the small bar below.
            <div className="wallp-batchload" role="status" aria-live="polite">
              <span className="wallp-loading-spin" />
              <span className="wallp-batchload-n">מכינים את המסגרות {cropsReady}/{batchNew.length}</span>
            </div>
          ) : null}

          {sheet !== "edit" && effectFor == null && cropFor == null && !batchProcessing && placedCount > 0 && (
            <nav className="wallp-mtoolbar">
              <div className="wallp-mtool-in">
                <button type="button" className={`wallp-mtool${sheet === "frame" ? " on" : ""}`} onClick={() => openSheet("frame")}><IconFrame /><span>מסגרת</span></button>
                <button type="button" className={`wallp-mtool${sheet === "effect" ? " on" : ""}`} onClick={() => { setEffectFor(null); openSheet("effect"); }}><IconEffect /><span>אפקט</span></button>
                <button type="button" className={`wallp-mtool${sheet === "photos" ? " on" : ""}`} onClick={() => openSheet("photos")}>
                  <span className="wallp-badge-wrap"><IconPhotos />{remaining > 0 ? <span className="wallp-missing-dot" aria-hidden="true" /> : null}</span><span>תמונות ({placedCount}/{count})</span>
                </button>
                <button type="button" className="wallp-mtool wallp-mtool-cta" onClick={goCheckout} disabled={busy}><IconCheck /><span>סיכום ואישור</span></button>
              </div>
            </nav>
          )}

          <div className={`wallp-sheet-ov${sheet ? " open" : ""}${sheet === "edit" ? " clear" : ""}`} onClick={closeSheets} />

          <section className={`wallp-sheet${sheet === "frame" ? " open" : ""}`}>
            <div className="sheet-head"><b>בחירת מסגרת</b><button type="button" className="sheet-done" onClick={closeSheets}>סיום</button></div>
            <div className="fcarousel">
              <button type="button" className="fcar-arrow start" aria-label="הקודם" onClick={() => scrollFcards(-1)}><svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg></button>
              <div className="fcards" ref={fcardsRef}><FrameRow cls="fcard" /></div>
              <button type="button" className="fcar-arrow end" aria-label="הבא" onClick={() => scrollFcards(1)}><svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" /></svg></button>
            </div>
          </section>

          <section className={`wallp-sheet${sheet === "effect" ? " open" : ""}`}>
            <div className="sheet-head"><b>בחירת אפקט</b><button type="button" className="sheet-done" onClick={closeSheets}>סיום</button></div>
            <div className="fcards">
              <button type="button" className={`fxcard${!effectCurBw ? " sel" : ""}`} onClick={() => applyEffect(false)}><img className="fxprev" src={effectPrev} alt="" /><span>מקורי</span></button>
              <button type="button" className={`fxcard${effectCurBw ? " sel" : ""}`} onClick={() => applyEffect(true)}><img className="fxprev bw" src={effectPrev} alt="" /><span>שחור לבן</span></button>
            </div>
          </section>

          {sheet === "edit" && (
            <div className="wallp-editbar">
              <div className="ebtn-wrap">
                <button type="button" className="ebtn" onClick={editCrop} aria-label="התאמת חיתוך"><svg viewBox="0 0 24 24"><path d="M6 2v3M6 8v8a2 2 0 002 2h8m4-4v-3m0-3V8a2 2 0 00-2-2H8m-6 0h3m5 16h3" /></svg></button>
                <span className="cap">חיתוך</span>
              </div>
              <div className="ebtn-wrap">
                <button type="button" className="ebtn" onClick={editEffect} aria-label="בחירת אפקט"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 000 18z" style={{ fill: "#087E63" }} /></svg></button>
                <span className="cap">אפקט</span>
              </div>
              <div className="ebtn-wrap">
                <button type="button" className="ebtn" onClick={editSwap} aria-label="החלפת מיקום"><svg viewBox="0 0 24 24"><path d="M7 8h11l-3-3M17 16H6l3 3" /></svg></button>
                <span className="cap">מיקום</span>
              </div>
              <div className="ebtn-wrap">
                <button type="button" className="ebtn" onClick={editReplace} aria-label="החלפת תמונה"><svg viewBox="0 0 24 24"><path d="M4 12a8 8 0 0113.6-5.6L20 8M20 12a8 8 0 01-13.6 5.6L4 16" /><path d="M20 4v4h-4M4 20v-4h4" /></svg></button>
                <span className="cap">החלפה</span>
              </div>
              <div className="ebtn-wrap del">
                <button type="button" className="ebtn del" onClick={editRemove} aria-label="הסרת תמונה"><svg viewBox="0 0 24 24"><path d="M6 7h12M9 7V5h6v2M8 7l1 13h6l1-13" /></svg></button>
                <span className="cap">מחיקה</span>
              </div>
            </div>
          )}

          <section className={`wallp-sheet${sheet === "photos" ? " open" : ""}`}>
            <div className="sheet-head"><b>{replaceFor != null ? "בחרו תמונה להחלפה" : "התמונות שלי"}</b><button type="button" className="sheet-done" onClick={closeSheets}>{replaceFor != null ? "ביטול" : "סיום"}</button></div>
            <div className={`pgrid${replaceFor != null ? " picking" : ""}`}>
              {pool.map((e) => {
                const on = placedIds.has(e.localId);
                // Gallery = the plain ORIGINAL, purely for viewing/selecting: no smart-crop
                // object-position, no server-crop swap, no effect filter. Nothing is refined
                // here, so nothing can jump — the thumbnail is static from first paint
                // (CSS object-fit:cover centers it). Face-cropping stays on the placed frame.
                // HEIC-safe (same guard as slotsView): only trust a local preview if it's
                // decodable (previewUrl, or objUrl whose dims we read); a HEIC on desktop
                // yields previewUrl=null & w=0, so fall back to the server original (JPEG)
                // instead of a blank cell.
                const local = e.previewUrl || (e.objUrl && e.w > 0 ? e.objUrl : null);
                const src = local || (e.record && (e.record.image || e.record.crop_image)) || null;
                // In replace mode only the SPARE (unplaced) photos are pickable; placed
                // ones already fill a frame, so they're dimmed and tap the current image.
                const dim = replaceFor != null && on;
                return src ? (
                  <div key={e.localId} className={`pcell${on ? " on" : ""}${e.pending ? " pending" : ""}${dim ? " dim" : ""}`} onClick={() => (replaceFor != null ? pickForReplace(e.localId) : togglePlace(e.localId))}>
                    <img src={src} alt="" />
                    {e.low ? <span className="pcell-lr" aria-label="תמונה באיכות נמוכה">{IconLowRes}</span> : null}
                    {on ? <span className="pchk"><svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg></span> : null}
                  </div>
                ) : null;
              })}
              {pool.length < MAXPOOL ? <button type="button" className="padd" aria-label="הוספת תמונה" onClick={() => (replaceFor != null ? replaceViaUpload() : openPicker(null))} /> : null}
            </div>
          </section>
        </div>
      </div>

      <div className="steps">
        <div className="step"><div className="n">1</div><strong>מעלים תמונות</strong><br /><span className="step-d">בוחרים {count} תמונות אהובות.</span></div>
        <div className="step"><div className="n">2</div><strong>מעצבים</strong><br /><span className="step-d">חותכים, מזיזים ובוחרים מסגרת.</span></div>
        <div className="step"><div className="n">3</div><strong>מדביקים לקיר</strong><br /><span className="step-d">מקבלים הביתה ומדביקים – בלי קידוח.</span></div>
      </div>

      <div className="dock">
        <div className="fsel"><FrameRow cls="fsel-item" /></div>
        <button type="button" className={`cta${full ? " ready" : ""}`} onClick={onCta} disabled={busy}>{ctaLabel}</button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFileChange} />

      <div className={`wallp-cart-ov${cartOpen ? " open" : ""}`} onClick={() => setCartOpen(false)} />
      <aside className={`wallp-cart${cartOpen ? " open" : ""}`}>
        <div className="cart-head"><span>סיכום הזמנה</span><button type="button" className="cart-x" onClick={() => setCartOpen(false)}>×</button></div>
        <div className="cc">
          <div className="cc-row"><span>{count} מסגרות · 20×20 ס"מ</span><span>₪{money(price.net)}</span></div>
          {price.savings > 0 ? <div className="cc-row disc"><span>הנחת כמות {price.discountPct}%</span><span>-₪{money(price.savings)}</span></div> : null}
          <div className="cc-row"><span>דמי טיפול</span><span>₪{money(price.handling)}</span></div>
          <div className="cc-row"><span>משלוח</span><span>{price.freeShip ? "חינם" : "₪35"}</span></div>
          <div className="cc-total"><span>סה"כ לתשלום</span><span>₪{money(price.final)}</span></div>
          <div className="cc-deliv">צפי משלוח: <b>{isMobile ? deliv.mobileRange : deliv.desktopRange}</b></div>
        </div>
        <button type="button" className="cart-checkout" onClick={goCheckout} disabled={busy}>{busy ? "מכינים…" : "מעבר לתשלום מאובטח"}</button>
      </aside>

      {/* initial: open at the SAME square the frame shows — the manual crop if any,
          else the smart/face region the preview's object-position was derived from.
          Without the region fallback the modal defaults to a center square while the
          frame shows the face square, so they read differently until the first crop. */}
      <CropModal
        open={cropFor != null}
        src={originalSrc(cropEntry)}
        frameName={frameName}
        sw={frameMeta.sw}
        bw={!!(cropEntry && cropEntry.bw)}
        initial={cropEntry ? (cropEntry.cropRegion || cropEntry.region || null) : null}
        onSave={onCropSave}
        onClose={() => { setCropFor(null); unfocus(); }}
      />

      {/* No upload-progress bar during onboarding: the onboarding form is the
          occupied wait (feels shorter) and its title "בזמן שאנחנו מכינים את המסגרות"
          already says the frames are being prepared — a floating bar would just
          duplicate that and compete with the form (UX research). When onboarding
          closes, the centering cover / small N/N pill take over for anything left. */}

      {/* First-upload onboarding — collect the user's info while photos upload.
          OnboardScreens self-gates on loginCompleted, so it no-ops once onboarded. */}
      {showOnboard ? <OnboardScreens wall /> : null}

      {/* Gallery-merge snackbar (Path B): offers to append a previous cart's
          images once the user's contact is known. Refreshes the pool on merge. */}
      <MergeSnackbar onMerged={refreshPoolFromServer} />

      {hint ? <div className="wallp-toast">{hint}</div> : null}
    </div>
  );
};

export default WallProduct;
